import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios";
import Cookies from "js-cookie";

class AxiosService {
  axiosInstance: AxiosInstance;

  multiPartFormDataConfig: AxiosRequestConfig = {
    baseURL: import.meta.env.VITE_APP_URL_BASE,
    headers: {
      "Content-Type": "multipart/form-data",
    },
  };

  constructor() {
    const axiosConfig: AxiosRequestConfig = {
      baseURL: import.meta.env.VITE_APP_URL_BASE,
      headers: {},
      withCredentials: true,
    };

    this.axiosInstance = axios.create(axiosConfig);

    this.axiosInstance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        if (!config.url) {
          return config;
        }

        const urlPath = this.getUrlPath(config.url);

        const excludedRoutes = ["/auth/"];

        const isExcluded = excludedRoutes.some((route) =>
          urlPath.startsWith(route)
        );

        if (!isExcluded) {
          const xsrfToken = Cookies.get("XSRF-TOKEN");
          if (xsrfToken && config.headers) {
            config.headers["X-XSRF-TOKEN"] = xsrfToken;
          } else {
            console.warn("XSRF-TOKEN não encontrado nos cookies.");
          }
        } else {
          console.debug(`Interceptor ignorando a rota: ${urlPath}`);
        }

        return config;
      },
      (error) => Promise.reject(error)
    );
  }

  private getUrlPath(url: string): string {
    try {
      const fullUrl = new URL(url, import.meta.env.VITE_APP_URL_BASE);
      return fullUrl.pathname;
    } catch (e) {
      return url;
    }
  }

  /**
   * Métodos HTTP
   */

  async get(path: string, params?: any): Promise<any> {
    return this.axiosInstance.get(path, { params });
  }

  async post(path: string, params?: any): Promise<any> {
    return this.axiosInstance.post(path, params);
  }

  async postT<Body, Result>(
    path: string,
    params?: Body,
    customConfig?: AxiosRequestConfig
  ): Promise<Result> {
    const response = await this.axiosInstance.post(path, params, customConfig);
    return response.data;
  }

  async put(path: string, params: any): Promise<any> {
    return this.axiosInstance.put(path, params);
  }

  async putT<Body, Result>(path: string, params: Body): Promise<Result> {
    const response = await this.axiosInstance.put(path, params);
    return response.data;
  }

  async delete(path: string, params?: any): Promise<any> {
    return this.axiosInstance.delete(path, { data: params });
  }

  async patch(path: string, params: any): Promise<any> {
    return this.axiosInstance.patch(path, params);
  }

  async head(path: string, params?: any): Promise<any> {
    return this.axiosInstance.head(path, { params });
  }

  /**
   * Upload de arquivos
   */
  async uploadFile(
    url: string,
    data: FormData,
    customConfig?: AxiosRequestConfig
  ): Promise<AxiosResponse> {
    const customAxiosRequestConfig =
      customConfig || this.multiPartFormDataConfig;
    return this.axiosInstance.post(url, data, customAxiosRequestConfig);
  }
}

const ApiService: AxiosService = new AxiosService();

export { ApiService };
